import {AdminSettings} from '@common/admin/settings/admin-settings';
import {PartialUploadType} from '@common/core/base-backend-bootstrap-data';
import {UploadingBackendSettings} from '@common/core/settings/base-backend-settings';
import {normalizeBooleanSetting} from '@ui/settings/normalize-boolean-setting';

export function buildUpdatedUploadingTypes(
  currentTypes: Record<string, PartialUploadType> | undefined,
  clientValues: AdminSettings['client'] | Partial<AdminSettings['client']>,
): Record<string, PartialUploadType> | undefined {
  const uploadingSettings = clientValues.uploading;

  if (!currentTypes || !uploadingSettings?.types) {
    return undefined;
  }

  const backendMap = new Map<string, UploadingBackendSettings>(
    (uploadingSettings.backends ?? []).map(backend => [backend.id, backend]),
  );

  return Object.fromEntries(
    Object.entries(currentTypes).map(([typeName, currentType]) => {
      const updatedType = uploadingSettings.types?.[typeName];
      if (!updatedType) {
        return [typeName, currentType];
      }

      const selectedBackendIds =
        updatedType.backends ?? currentType.backends.map(backend => backend.id);
      const resolvedBackends = selectedBackendIds
        .map(backendId =>
          mapBackendForBootstrap(
            backendMap.get(backendId),
            currentType.backends.find(backend => backend.id === backendId),
          ),
        )
        .filter(backend => backend !== null);

      return [
        typeName,
        {
          ...currentType,
          max_file_size: updatedType.max_file_size ?? currentType.max_file_size,
          accept: updatedType.accept ?? currentType.accept,
          backends: resolvedBackends,
        },
      ];
    }),
  );
}

function mapBackendForBootstrap(
  backend: UploadingBackendSettings | undefined,
  fallback: PartialUploadType['backends'][number] | undefined,
): PartialUploadType['backends'][number] | null {
  if (!backend) {
    return fallback ?? null;
  }

  const driver = ['s3_compatible', 'digitalocean', 'backblaze'].includes(
    backend.type,
  )
    ? 's3'
    : backend.type;

  return {
    id: backend.id,
    driver,
    ...(driver === 's3'
      ? {
          direct_upload: normalizeBooleanSetting(
            backend.config?.direct_upload,
          ),
        }
      : {}),
  };
}
